import { useState, useRef, useEffect, useMemo } from "react";

export type SearchOption = { value: number | string; label: string; hint?: string };

/**
 * 可搜索单选下拉：输入关键字实时过滤 label/hint。用户/报告等长列表选择用。
 * 受控：value 为当前选中值（null 未选）。复用 admin globals.css 的 .ce-input。
 */
export function SearchSelect({ value, options, onChange, placeholder, allowClear = true }: {
  value: number | string | null;
  options: SearchOption[];
  onChange: (v: number | string | null) => void;
  placeholder?: string;
  allowClear?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value) || null;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options.slice(0, 100);
    return options.filter((o) => o.label.toLowerCase().includes(s) || (o.hint || "").toLowerCase().includes(s)).slice(0, 100);
  }, [q, options]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        className="ce-input"
        value={open ? q : (selected ? selected.label : "")}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQ(""); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
      />
      {allowClear && selected && !open && (
        <button type="button" onClick={() => onChange(null)}
          style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "var(--muted, #888)", fontSize: 14 }}>×</button>
      )}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, zIndex: 50,
          maxHeight: 260, overflowY: "auto", background: "var(--panel, #fff)",
          border: "1px solid var(--line, #ddd)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        }}>
          {allowClear && (
            <div onClick={() => { onChange(null); setOpen(false); }}
              style={{ padding: "9px 12px", cursor: "pointer", fontSize: 13, color: "var(--muted, #888)", borderBottom: "1px solid var(--line, #eee)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.04)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              — 不选 —
            </div>
          )}
          {filtered.length === 0 && <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--muted, #888)" }}>无匹配</div>}
          {filtered.map((o) => (
            <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
              style={{
                padding: "9px 12px", cursor: "pointer", fontSize: 13,
                background: o.value === value ? "rgba(184,134,11,0.10)" : "transparent",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(184,134,11,0.06)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = o.value === value ? "rgba(184,134,11,0.10)" : "transparent")}>
              {o.label}
              {o.hint && <span style={{ color: "var(--muted, #999)", fontSize: 11, marginLeft: 8 }}>{o.hint}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
